import { useCallback, useRef, useState } from 'react';
import type { CheckResponse } from '@domaintriage/shared';
import { checkDomains } from '../api/check';

export interface UseDomainCheckReturn {
  /** Perform a domain check. Returns the response on success. */
  check: (domains: string[], tlds?: string[]) => Promise<CheckResponse>;
  /** Whether a check is currently in flight. */
  isLoading: boolean;
  /** Human-readable error message from the last failed check, if any. */
  error: string | null;
  /** Response from the last successful check, if any. */
  results: CheckResponse | null;
  /** Cancel the in-flight request, if any. */
  cancel: () => void;
}

/**
 * Hook that wraps {@link checkDomains} with loading/error state and an
 * `AbortController` for cancellation.
 */
export function useDomainCheck(): UseDomainCheckReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<CheckResponse | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const check = useCallback(
    async (domains: string[], tlds?: string[]): Promise<CheckResponse> => {
      // Abort any previous in-flight request.
      cancel();

      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        const response = await checkDomains(
          { domains, tlds },
          controller.signal,
        );
        setResults(response);
        return response;
      } catch (err: unknown) {
        // Swallow abort errors — the caller already knows they cancelled.
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }

        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        setError(message);
        throw err;
      } finally {
        // Only clear loading if this controller is still the active one (i.e.
        // a newer call hasn't replaced it).
        if (abortRef.current === controller) {
          setIsLoading(false);
          abortRef.current = null;
        }
      }
    },
    [cancel],
  );

  return { check, isLoading, error, results, cancel };
}
